import React from 'react';
import { colors, typography, spacing } from '../../styles/design-system';

export type StatusType = 'success' | 'error' | 'warning' | 'info' | 'default';

interface StatusBadgeProps {
  status: StatusType;
  label: string;
  variant?: 'text' | 'badge';
  size?: 'sm' | 'md';
  style?: React.CSSProperties;
}

// Predefined status mappings for common Korean status values
export const STATUS_MAPPINGS: Record<string, StatusType> = {
  // Korean statuses
  '완료': 'success',
  '성공': 'success',
  '활성': 'success',
  '진행중': 'warning',
  '대기': 'warning',
  '경기전': 'warning',
  '실패': 'error',
  '오류': 'error',
  '에러': 'error',
  '비활성': 'default',
  '취소': 'default',
  // English statuses
  'success': 'success',
  'completed': 'success',
  'active': 'success',
  'pending': 'warning',
  'in_progress': 'warning',
  'pre_match': 'warning',
  'failed': 'error',
  'error': 'error',
  'inactive': 'default',
  'cancelled': 'default',
  // Action types
  'created': 'success',
  'updated': 'info',
  'skipped': 'warning',
};

// Helper function to determine status type from string
export const getStatusType = (status: string): StatusType => {
  const normalized = status.toLowerCase();
  return STATUS_MAPPINGS[status] || STATUS_MAPPINGS[normalized] || 'default';
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  variant = 'text',
  size = 'md',
  style,
}) => {
  const statusColors: Record<StatusType, string> = {
    success: colors.status.success,
    error: colors.status.error,
    warning: colors.status.warning,
    info: colors.status.info,
    default: colors.gray[500],
  };

  const baseStyle: React.CSSProperties = {
    fontSize: size === 'sm' ? typography.fontSize.xs : typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    whiteSpace: 'nowrap',
  };

  if (variant === 'badge') {
    return (
      <span
        style={{
          ...baseStyle,
          display: 'inline-block',
          padding: size === 'sm' ? `${spacing[1]} ${spacing[2]}` : `${spacing[1]} ${spacing[3]}`,
          backgroundColor: statusColors[status],
          color: colors.white,
          ...style,
        }}
      >
        {label}
      </span>
    );
  }

  // text variant (default)
  return (
    <span
      style={{
        ...baseStyle,
        color: statusColors[status],
        ...style,
      }}
    >
      {label}
    </span>
  );
};

// Convenience component for auto-detecting status type
interface AutoStatusBadgeProps {
  status: string;
  variant?: 'text' | 'badge';
  size?: 'sm' | 'md';
  labelMap?: Record<string, string>;
  style?: React.CSSProperties;
}

export const AutoStatusBadge: React.FC<AutoStatusBadgeProps> = ({
  status,
  variant = 'text',
  size = 'md',
  labelMap,
  style,
}) => {
  const statusType = getStatusType(status);
  const label = labelMap?.[status] || status;

  return (
    <StatusBadge
      status={statusType}
      label={label}
      variant={variant}
      size={size}
      style={style}
    />
  );
};

export default StatusBadge;
